import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Ticket } from "lucide-react";
import { Input } from "./components/ui/input";

const useCategoryAvailability = (eventId, enabled) => {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    if (!enabled || !eventId) return;
    axios.get(`events/${eventId}/category-availability`)
      .then((r) => setCats(r.data?.categories || []))
      .catch(() => setCats([]));
  }, [eventId, enabled]);
  return cats;
};

/** Admin picker: which ticket category (and how many tickets) a manual entry uses. Warns, never blocks. */
export const TicketCategoryPicker = ({ eventId, open = true, value, onChange, count, onCountChange, testIdPrefix, countsWhenPaid = false }) => {
  const cats = useCategoryAvailability(eventId, open);
  if (cats.length === 0) return null;
  const picked = cats.find((c) => c.row_id === value);
  const want = onCountChange ? Number(count) || 0 : 1;
  const over = picked && picked.remaining != null && want > picked.remaining;

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-category-picker`}>
      <label className="text-sm font-medium text-slate-700">Ticket category <span className="text-xs text-slate-500 font-normal">(optional)</span></label>
      <div className="flex gap-2">
        <select
          className="flex-1 h-9 rounded-md border border-slate-200 text-sm px-2 bg-white"
          value={value || ""}
          onChange={(e) => onChange(e.target.value, cats.find((c) => c.row_id === e.target.value))}
          data-testid={`${testIdPrefix}-category-select`}
        >
          <option value="">No specific category</option>
          {cats.map((c) => (
            <option key={c.row_id} value={c.row_id}>
              {c.label}{c.remaining == null ? "" : c.remaining === 0 ? " (full)" : ` (${c.remaining} left)`}
            </option>
          ))}
        </select>
        {onCountChange && value && (
          <Input
            type="number"
            min="1"
            step="1"
            className="w-24 h-9"
            value={count ?? ""}
            onChange={(e) => onCountChange(e.target.value)}
            placeholder="Tickets"
            title="Number of tickets"
            data-testid={`${testIdPrefix}-ticket-count`}
          />
        )}
      </div>
      {over && (
        <p className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid={`${testIdPrefix}-category-warning`}>
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          {picked.remaining === 0
            ? `${picked.label} is already full (limit ${picked.capacity}).`
            : `Only ${picked.remaining} left in ${picked.label} (limit ${picked.capacity}).`}
          {countsWhenPaid ? " If the member pays, the category will go over its limit." : " Saving will take the category over its limit."}
        </p>
      )}
    </div>
  );
};

/** Event Financials: sold / remaining per ticket category. */
export const CategoryAvailabilityCard = ({ categories = [] }) => {
  if (!categories.some((c) => c.capacity != null)) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="category-availability-card">
      <div className="flex items-center gap-2 mb-3">
        <Ticket className="h-4 w-4 text-emerald-700" />
        <h3 className="text-sm font-semibold text-slate-900">Tickets per category (paid)</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {categories.map((c) => {
          const full = c.remaining === 0;
          const over = c.capacity != null && c.sold > c.capacity;
          return (
            <div key={c.row_id} className={`rounded-md border p-3 ${full ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50"}`} data-testid={`category-availability-${c.row_id}`}>
              <p className="text-xs text-slate-600 truncate">{c.label}</p>
              <p className="text-lg font-bold text-slate-900">
                {c.sold}{c.capacity != null && <span className="text-sm font-normal text-slate-500"> / {c.capacity}</span>}
              </p>
              <p className={`text-xs ${full ? "text-red-700 font-semibold" : "text-slate-500"}`}>
                {c.capacity == null ? "No limit" : over ? `Over limit by ${c.sold - c.capacity}` : full ? "Sold out" : `${c.remaining} left`}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
